import React, { useState, useEffect, useMemo, useCallback } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useEmployees } from "../../../hooks/useEmployees";
import { distributionApi } from "../../../services/api/distribution";
import { useLocationContext } from "../../../contexts/LocationContext";

/**
 * Employee Statements tab — per-employee TRONC statement view with
 * search, date-range filtering, and CSV export.
 */
const EmployeeStatementsTab = ({ shouldFetchData }) => {
  const { currentLocation } = useLocationContext();
  const { employees, loading: loadingEmployees } = useEmployees({
    autoFetch: shouldFetchData,
    activeOnly: true,
    troncEligibleOnly: true,
  });

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [search, setSearch] = useState("");
  const [allocations, setAllocations] = useState([]);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [dateRange, setDateRange] = useState("30days");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const getDateBounds = useCallback(() => {
    const now = new Date();
    let from = new Date();
    switch (dateRange) {
      case "7days":
        from.setDate(now.getDate() - 7);
        break;
      case "30days":
        from.setDate(now.getDate() - 30);
        break;
      case "thisMonth":
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "lastMonth":
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return { from, to: new Date(now.getFullYear(), now.getMonth(), 0) };
      case "custom":
        if (customFrom && customTo) {
          return { from: new Date(customFrom), to: new Date(new Date(customTo).setHours(23, 59, 59, 999)) };
        }
        from.setDate(now.getDate() - 30);
        break;
      default:
        from.setDate(now.getDate() - 30);
    }
    return { from, to: now };
  }, [dateRange, customFrom, customTo]);

  // Fetch allocations when an employee is selected
  useEffect(() => {
    if (!selectedEmployeeId) {
      setAllocations([]);
      return;
    }
    const load = async () => {
      setLoadingAllocations(true);
      try {
        const { from, to } = getDateBounds();
        const res = await distributionApi.getEmployeeAllocations(
          selectedEmployeeId,
          {
            fromDate: from.toISOString(),
            toDate: to.toISOString(),
          },
        );
        const data = res?.data ?? res?.Data ?? [];
        setAllocations(Array.isArray(data) ? data : []);
      } catch {
        setAllocations([]);
      } finally {
        setLoadingAllocations(false);
      }
    };
    load();
  }, [selectedEmployeeId, dateRange, getDateBounds]);

  const filteredEmployees = useMemo(() => {
    let list = employees || [];
    if (currentLocation) {
      list = list.filter((e) => e.locationId === currentLocation.id);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
          (e.email || "").toLowerCase().includes(q) ||
          (e.payrollNumber || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [employees, search, currentLocation]);

  const selectedEmployee = useMemo(
    () => (employees || []).find((e) => e.id === selectedEmployeeId),
    [employees, selectedEmployeeId],
  );

  const totalTronc = useMemo(
    () => allocations.reduce((sum, a) => sum + (a.amount ?? a.Amount ?? 0), 0),
    [allocations],
  );

  const handleExportStatement = () => {
    if (!selectedEmployee || allocations.length === 0) return;
    const headers = [
      "Date",
      "Amount (£)",
      "Job Role",
      "Period",
      "Distribution Rule",
      "Override Note",
    ];
    const rows = allocations.map((a) => [
      new Date(a.periodStart ?? a.allocatedOn).toLocaleDateString("en-GB"),
      (a.amount ?? a.Amount ?? 0).toFixed(2),
      a.jobRoleName || "—",
      a.periodStart && a.periodEnd
        ? `${new Date(a.periodStart).toLocaleDateString("en-GB")} – ${new Date(a.periodEnd).toLocaleDateString("en-GB")}`
        : "—",
      a.distributionRuleName || "—",
      a.overrideNote || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tronc-statement-${selectedEmployee.firstName}-${selectedEmployee.lastName}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const dateRangeOptions = [
    { id: "7days", label: "Last 7 Days" },
    { id: "30days", label: "Last 30 Days" },
    { id: "thisMonth", label: "This Month" },
    { id: "lastMonth", label: "Last Month" },
    { id: "custom", label: "Custom Range" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Employee list */}
      <div className="lg:col-span-1">
        <div className="card">
          <div className="p-4 border-b border-border">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Icon
                  name="Search"
                  size={16}
                  className="text-muted-foreground"
                />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employees..."
                className="input !pl-10 text-sm"
              />
            </div>
          </div>
          <div className="max-h-[500px] overflow-y-auto divide-y divide-border">
            {loadingEmployees ? (
              <div className="p-6 text-center">
                <div className="spinner mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Loading employees...
                </p>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No eligible employees found.
              </div>
            ) : (
              filteredEmployees.map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => setSelectedEmployeeId(emp.id)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-base hover:bg-muted/50 ${selectedEmployeeId === emp.id ? "bg-primary/5 border-l-2 border-primary" : ""}`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                    {emp.firstName?.[0]}
                    {emp.lastName?.[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {emp.firstName} {emp.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {emp.jobTitle || emp.department || emp.email || "—"}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Statement detail */}
      <div className="lg:col-span-2">
        {!selectedEmployeeId ? (
          <div className="card p-10 text-center">
            <div className="empty-state-icon mx-auto mb-4">
              <Icon name="Users" size={32} />
            </div>
            <h3 className="text-lg font-semibold mb-2">Select an Employee</h3>
            <p className="text-muted-foreground">
              Choose an employee from the list to view their TRONC statement.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Header */}
            <div className="card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {selectedEmployee?.firstName} {selectedEmployee?.lastName}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedEmployee?.jobTitle || selectedEmployee?.email || "—"}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex flex-wrap gap-2">
                  {dateRangeOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setDateRange(opt.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-base ${dateRange === opt.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {dateRange === "custom" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs text-muted-foreground">From</label>
                      <input
                        type="date"
                        value={customFrom}
                        max={customTo || undefined}
                        onChange={(e) => setCustomFrom(e.target.value)}
                        className="input text-xs py-1 px-2 h-auto"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs text-muted-foreground">To</label>
                      <input
                        type="date"
                        value={customTo}
                        min={customFrom || undefined}
                        onChange={(e) => setCustomTo(e.target.value)}
                        className="input text-xs py-1 px-2 h-auto"
                      />
                    </div>
                    {(!customFrom || !customTo) && (
                      <p className="text-xs text-muted-foreground italic">Select both dates</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="card p-4 text-center">
                <p className="text-xs text-muted-foreground mb-1">
                  Total TRONC
                </p>
                <p className="text-xl font-bold text-foreground">
                  £{totalTronc.toFixed(2)}
                </p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-xs text-muted-foreground mb-1">
                  Distributions
                </p>
                <p className="text-xl font-bold text-foreground">
                  {allocations.length}
                </p>
              </div>
              <div className="card p-4 text-center hidden sm:block">
                <p className="text-xs text-muted-foreground mb-1">
                  Avg per Distribution
                </p>
                <p className="text-xl font-bold text-foreground">
                  £
                  {allocations.length > 0
                    ? (totalTronc / allocations.length).toFixed(2)
                    : "0.00"}
                </p>
              </div>
            </div>

            {/* Allocations table */}
            <div className="card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground">
                  Allocation History
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExportStatement}
                  disabled={allocations.length === 0}
                >
                  Export CSV
                </Button>
              </div>

              {loadingAllocations ? (
                <div className="p-8 text-center">
                  <div className="spinner mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Loading allocations...
                  </p>
                </div>
              ) : allocations.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No TRONC allocations found for this period.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground">
                          Date
                        </th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-foreground">
                          Amount
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden sm:table-cell">
                          Role
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden md:table-cell">
                          Period
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden lg:table-cell">
                          Rule
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {allocations.map((a, idx) => (
                        <tr
                          key={a.id ?? idx}
                          className="hover:bg-muted/30 transition-base"
                        >
                          <td className="px-5 py-3 text-sm text-foreground">
                            {new Date(
                              a.periodStart ?? a.allocatedOn,
                            ).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </td>
                          <td className="px-5 py-3 text-sm font-semibold text-foreground text-right">
                            £{(a.amount ?? a.Amount ?? 0).toFixed(2)}
                          </td>
                          <td className="px-5 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                            {a.jobRoleName || "—"}
                          </td>
                          <td className="px-5 py-3 text-sm text-muted-foreground hidden md:table-cell">
                            {a.periodStart && a.periodEnd
                              ? `${new Date(a.periodStart).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${new Date(a.periodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                              : "—"}
                          </td>
                          <td className="px-5 py-3 text-sm text-muted-foreground hidden lg:table-cell">
                            <div>
                              {a.distributionRuleName || "—"}
                              {a.isManualOverride && (
                                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-warning/10 text-warning">
                                  Override
                                </span>
                              )}
                            </div>
                            {a.overrideNote && (
                              <p className="text-xs text-warning mt-1">
                                {a.overrideNote}
                              </p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeStatementsTab;
